import React from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Plus, Trash2 } from 'lucide-react';
import { useStorefrontData } from '../context/StorefrontDataContext';
import { formatEditorialText } from './EditorialSections';
import EditableText from './live-editor/EditableText';
import EditableRichText from './live-editor/EditableRichText';

// Helper to format title and dynamically italicize/color the last word
export function formatFAQTitle(title: string | undefined): string {
  if (!title) return '';
  
  const trimmed = title.trim();
  // If it already has rich formatting tags, let formatEditorialText handle it
  if (trimmed.includes('*') || trimmed.includes('_') || trimmed.includes('[wine]') || trimmed.includes('<')) {
    return formatEditorialText(trimmed);
  }
  
  const words = trimmed.split(/\s+/);
  if (words.length <= 1) {
    return `<span class="italic text-brand-wine">${trimmed}</span>`;
  }
  
  const lastWord = words.pop();
  const firstPart = words.join(' ');
  return `${firstPart} <span class="italic text-brand-wine">${lastWord}</span>`;
}

export default function FAQSection() {
  const { cms, isReady, isAdmin, isEditMode, isPreviewMode, updateCmsField, updateCmsSection } = useStorefrontData();
  const shouldReduceMotion = useReducedMotion();

  const canEdit = isAdmin && isEditMode && !isPreviewMode;

  // Animation presets in line with Premium Editorial style
  const fadeUp = {
    initial: shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-100px' },
    transition: { duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] as const }
  };

  const faq = cms?.faq || { title: '', description: '', items: [] };
  const faqItems = faq.items || [];

  const handleUpdateItem = (index: number, field: 'question' | 'answer', val: string) => {
    const nextItems = [...faqItems];
    nextItems[index] = { ...nextItems[index], [field]: val };
    updateCmsField('faq', 'items', nextItems);
  };

  const handleDeleteItem = (index: number) => {
    if (window.confirm('Hapus pertanyaan FAQ ini?')) {
      const nextItems = faqItems.filter((_: any, i: number) => i !== index);
      updateCmsField('faq', 'items', nextItems);
    }
  };

  const handleAddItem = () => {
    const newItem = {
      id: `faq-${Date.now()}`,
      question: 'Pertanyaan baru...',
      answer: 'Tuliskan jawaban lengkap di sini.'
    };
    updateCmsField('faq', 'items', [...faqItems, newItem]);
  };

  if (!isReady) {
    return (
      <section id="faq-section" className="w-full bg-[#FCFCFB] border-t border-ink/5 py-24 px-6 md:px-12 lg:px-20">
        <div className="max-w-[1300px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 animate-quiet-pulse">
          {/* Skeleton Left */}
          <div className="lg:col-span-4 space-y-5">
            <div className="h-3 bg-zinc-200/60 w-24" />
            <div className="h-10 bg-zinc-200/60 w-48" />
            <div className="w-12 h-[1px] bg-zinc-200/30" />
            <div className="h-16 bg-zinc-200/60 w-full" />
          </div>
          {/* Skeleton Right */}
          <div className="lg:col-span-8 space-y-12">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-3 pb-8 border-b border-zinc-200/30">
                <div className="h-4 bg-zinc-200/40 w-2/3" />
                <div className="h-12 bg-zinc-200/40 w-full" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (faqItems.length === 0 && !canEdit) return null;

  return (
    <section id="faq-section" className="w-full bg-[#FCFCFB] border-t border-ink/5 py-24 px-6 md:px-12 lg:px-20">
      <div className="max-w-[1300px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
        
        {/* Left Column: Asymmetric Brand Header */}
        <div className="lg:col-span-4 space-y-5 lg:sticky lg:top-[120px] h-fit">
          <span className="text-[10px] font-black text-brand-wine uppercase tracking-[0.25em] block mb-2 select-none">
            TANYA JAWAB
          </span>

          {canEdit ? (
            <EditableRichText
              value={faq.title}
              onChange={(val) => updateCmsField('faq', 'title', val)}
              as="h2"
              multiline
              label="Judul FAQ"
              className="text-3xl md:text-4xl font-serif text-ink tracking-tight leading-[1.1] font-light"
            />
          ) : (
            <h2 
              className="text-3xl md:text-4xl font-serif text-ink tracking-tight leading-[1.1] font-light rich-text-content"
              dangerouslySetInnerHTML={{ __html: formatFAQTitle(faq.title) }}
            />
          )}

          <div className="w-12 h-[1px] bg-brand-wine/20 mt-6 hidden lg:block" />

          {canEdit ? (
            <EditableRichText
              value={faq.description || ''}
              onChange={(val) => updateCmsField('faq', 'description', val)}
              as="p"
              multiline
              placeholder="Deskripsi pengantar FAQ..."
              label="Deskripsi FAQ"
              className="text-[11px] text-muted/80 leading-relaxed max-w-sm pt-2"
            />
          ) : faq.description ? (
            <div
              className="text-[11px] text-muted/60 leading-relaxed max-w-sm pt-2"
              dangerouslySetInnerHTML={{ __html: faq.description }}
            />
          ) : null}
        </div>

        {/* Right Column: Q&A Stack with Diamond Icons & Clean Borders */}
        <div className="lg:col-span-8 space-y-12">
          {faqItems.map((item: any, index: number) => (
            <motion.div 
              key={item.id || index}
              {...fadeUp}
              className="space-y-3 pb-8 border-b border-ink/[0.06] last:border-b-0 last:pb-0 relative group"
            >
              {canEdit ? (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 flex items-start gap-2">
                      <span className="text-brand-wine shrink-0 text-[10px] mt-1 select-none">✦</span>
                      <EditableText
                        value={item.question}
                        onChange={(val) => handleUpdateItem(index, 'question', val)}
                        as="h3"
                        label={`Pertanyaan #${index + 1}`}
                        className="text-xs font-bold text-ink uppercase tracking-wider leading-snug"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteItem(index)}
                      className="p-1 text-muted hover:text-red-600 transition-colors cursor-pointer shrink-0"
                      title="Hapus Pertanyaan FAQ"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="pl-6">
                    <EditableRichText
                      value={item.answer}
                      onChange={(val) => handleUpdateItem(index, 'answer', val)}
                      as="div"
                      multiline
                      label={`Jawaban #${index + 1}`}
                      className="text-xs text-muted/80 leading-relaxed font-normal"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <h3 className="text-xs font-bold text-ink uppercase tracking-wider flex items-start gap-3 leading-snug">
                    <span className="text-brand-wine shrink-0 text-[10px] mt-0.5 select-none">✦</span>
                    {item.question}
                  </h3>
                  <div 
                    className="pl-6 text-xs text-muted/80 leading-relaxed font-normal rich-text-content"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </>
              )}
            </motion.div>
          ))}

          {/* Add FAQ Item Button (Edit mode only) */}
          {canEdit && (
            <button
              type="button"
              onClick={handleAddItem}
              className="flex items-center gap-2 px-4 py-2.5 bg-brand-wine/5 hover:bg-brand-wine/10 text-brand-wine border border-dashed border-brand-wine/30 rounded-xl text-xs font-bold transition-all cursor-pointer w-full justify-center"
            >
              <Plus className="w-4 h-4" />
              <span>Tambah Pertanyaan FAQ Baru</span>
            </button>
          )}
        </div>

      </div>
    </section>
  );
}
